import { useState, useRef, useCallback } from "preact/hooks";
import type { JSX } from "preact";

const DEFAULT_DELAY_MS = 400;

interface TooltipProps {
  children: JSX.Element | JSX.Element[];
  /** Tooltip content (string or Preact node). */
  content: JSX.Element | string | null;
  /** Delay in ms before showing. Default 400. */
  delayMs?: number;
  /** Allow multiple lines (e.g. list of slots). Default false. */
  multiline?: boolean;
  /** Optional class for the wrapper. */
  className?: string;
}

/**
 * Brutalist tooltip: shows content after a short delay on hover.
 * Styled with border-2, brutal shadow, monospace.
 */
export default function Tooltip({
  children,
  content,
  delayMs = DEFAULT_DELAY_MS,
  multiline = false,
  className = "",
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    clearTimer();
    timeoutRef.current = setTimeout(() => setVisible(true), delayMs);
  }, [delayMs, clearTimer]);

  const hide = useCallback(() => {
    clearTimer();
    setVisible(false);
  }, [clearTimer]);

  if (content == null || content === "") {
    return <>{children}</>;
  }

  return (
    <span
      className={`relative inline-block ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {visible ? (
        <span
          className={`absolute left-1/2 bottom-full -translate-x-1/2 mb-1.5 z-50 px-2 py-1.5 text-xs font-mono border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 brutal-tooltip-shadow pointer-events-none ${
            multiline
              ? "whitespace-normal text-left max-w-[min(90vw,40rem)]"
              : "whitespace-nowrap overflow-hidden text-ellipsis max-w-[min(90vw,20rem)]"
          }`}
          role="tooltip"
        >
          {typeof content === "string" ? content : content}
        </span>
      ) : null}
    </span>
  );
}
